import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AdAuction = () => {
    const [adSpaces, setAdSpaces] = useState([]);
    const [selectedAdSpace, setSelectedAdSpace] = useState(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentData, setPaymentData] = useState({
        amount: '',
        contract_duration: 1,
        needs_design_help: false,
        creative_brief: ''
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchAdSpaces();
    }, []);

    const fetchAdSpaces = async () => {
        try {
            const response = await axios.get('/api/adspaces');
            setAdSpaces(response.data);
        } catch (error) {
            console.error('Error fetching ad spaces:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAdSpaceSelect = (adSpace) => {
        setSelectedAdSpace(adSpace);
        setPaymentData({
            ...paymentData,
            amount: adSpace.auction_price
        });
        setShowPaymentModal(true);
    };

    const handlePaymentSubmit = async (e) => {
        e.preventDefault();
        try {
            // Create payment intent
            const response = await axios.post('/api/payments/create-intent', {
                amount: parseFloat(paymentData.amount),
                client_id: 1, // This would come from authentication
                contract_duration: paymentData.contract_duration,
                needs_design_help: paymentData.needs_design_help
            });

            if (response.data.client_secret) {
                // In a real implementation, you would use Stripe.js to handle the payment
                alert(`Payment intent created! Amount: $${response.data.amount}, Discount: ${response.data.discount_percent}%`);
                setShowPaymentModal(false);
                setPaymentData({
                    amount: '',
                    contract_duration: 1,
                    needs_design_help: false,
                    creative_brief: ''
                });
            }
        } catch (error) {
            console.error('Error creating payment intent:', error);
            alert('Failed to create payment intent');
        }
    };

    const calculateDiscount = (duration) => {
        if (duration >= 36) return 10; // 3 years
        if (duration >= 12) return 5;  // 1 year
        return 0;
    };

    const calculateFinalAmount = (baseAmount, duration) => {
        const discount = calculateDiscount(duration);
        return baseAmount * (1 - discount / 100);
    };

    if (loading) {
        return <div className="ad-auction loading">Loading ad spaces...</div>;
    }

    return (
        <div className="ad-auction">
            <div className="auction-header">
                <h1>Ad Auction Platform</h1>
                <p>Bid on premium advertising spaces and reach your target audience</p>
            </div>

            <div className="auction-info">
                <div className="info-card">
                    <h3>Contract Discounts</h3>
                    <ul>
                        <li>1 Year Contract: 5% discount</li>
                        <li>3 Year Contract: 10% discount</li>
                    </ul>
                </div>
                <div className="info-card">
                    <h3>Design Services</h3>
                    <p>Need help with your ad design? Our professional team can help for an additional $500 fee.</p>
                </div>
                <div className="info-card">
                    <h3>Cancellation Policy</h3>
                    <p>40% cancellation fee applies to early contract termination.</p>
                </div>
            </div>

            <div className="ad-spaces-grid">
                {adSpaces.map(adSpace => (
                    <div key={adSpace.id} className="ad-space-card">
                        <div className="ad-space-header">
                            <h3>{adSpace.name}</h3>
                            <span className="location">{adSpace.location}</span>
                        </div>
                        <p className="description">{adSpace.description}</p>
                        <div className="ad-space-details">
                            <p><strong>Starting Bid:</strong> ${adSpace.auction_price}</p>
                            <p><strong>Display Frequency:</strong> {adSpace.display_frequency}x per day</p>
                        </div>
                        <div className="ad-space-actions">
                            <button 
                                onClick={() => handleAdSpaceSelect(adSpace)}
                                className="btn btn-primary"
                            >
                                Bid Now
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Payment Modal */}
            {showPaymentModal && selectedAdSpace && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>Purchase Ad Space: {selectedAdSpace.name}</h3>
                        <form onSubmit={handlePaymentSubmit}>
                            <div className="form-group">
                                <label>Contract Duration (months):</label>
                                <select
                                    value={paymentData.contract_duration}
                                    onChange={(e) => setPaymentData({
                                        ...paymentData,
                                        contract_duration: parseInt(e.target.value)
                                    })}
                                >
                                    <option value="1">1 Month</option>
                                    <option value="3">3 Months</option>
                                    <option value="6">6 Months</option>
                                    <option value="12">1 Year (5% discount)</option>
                                    <option value="36">3 Years (10% discount)</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Base Amount:</label>
                                <input
                                    type="number"
                                    value={paymentData.amount}
                                    onChange={(e) => setPaymentData({
                                        ...paymentData,
                                        amount: e.target.value
                                    })}
                                    required
                                />
                            </div>

                            <div className="discount-info">
                                <p><strong>Discount:</strong> {calculateDiscount(paymentData.contract_duration)}%</p>
                                <p><strong>Final Amount:</strong> ${calculateFinalAmount(parseFloat(paymentData.amount), paymentData.contract_duration).toFixed(2)}</p>
                            </div>

                            <div className="form-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={paymentData.needs_design_help}
                                        onChange={(e) => setPaymentData({
                                            ...paymentData,
                                            needs_design_help: e.target.checked
                                        })}
                                    />
                                    Need design help? (+$500)
                                </label>
                            </div>

                            {paymentData.needs_design_help && (
                                <div className="form-group">
                                    <label>Creative Brief:</label>
                                    <textarea
                                        value={paymentData.creative_brief}
                                        onChange={(e) => setPaymentData({
                                            ...paymentData,
                                            creative_brief: e.target.value
                                        })}
                                        placeholder="Describe your vision, target audience, brand guidelines, etc."
                                        rows="4"
                                    />
                                </div>
                            )}

                            <div className="contract-terms">
                                <h4>Contract Terms</h4>
                                <ul>
                                    <li>Contract duration: {paymentData.contract_duration} months</li>
                                    <li>Discount: {calculateDiscount(paymentData.contract_duration)}%</li>
                                    <li>Cancellation fee: 40% of remaining value</li>
                                    <li>Design service: {paymentData.needs_design_help ? 'Included (+$500)' : 'Not included'}</li>
                                </ul>
                            </div>

                            <div className="modal-actions">
                                <button type="submit" className="btn btn-primary">
                                    Proceed to Payment
                                </button>
                                <button 
                                    type="button" 
                                    onClick={() => setShowPaymentModal(false)} 
                                    className="btn btn-secondary"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdAuction;